import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { Helmet, HelmetTags } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { selectPciPaymentApiEnabled } from 'src/store/slices/config/configSelectors';
import dayjs from 'dayjs';
import GeneralPageLayout from '../../../../components/layout/GeneralPageLayout';
import { MicroFormPlaceholder } from '../../../../components/payment/cybersource/types';
import usePlaceNavigate from '../../../../hooks/usePlaceNavigate';
import { buildPaymentApi } from '../../../../api';
import CybersourceConstants from './constants';
import {
  CustomPaymentFormElements,
  CustomPaymentData,
  CustomPaymentForm,
} from '../../../../components/payment/custom-form';
import { convertNumberToCurrency } from '../../../../common/utils';
import { selectPayment } from '../../../..//store/slices/payment/paymentSelectors';
import { selectCurrentFlow } from '../../../..//store/slices/flow/flowSelectors';
import { checkPaymentAsync } from '../../../../store/slices/payment/paymentThunk';
import { useAppDispatch } from '../../../../store/store';
import { useCloseWindow } from '../../../../hooks/useCloseWindow';
import { FLOWS_ENUM } from '../../../../common/enums';
import { PostbackStatus } from '../../../../types/payment';
import { FormikErrors, FormikHelpers, FormikValues } from 'formik';
import { FormValidations } from 'src/components/form/constants';

const months = new Array(12).fill(1).map((_, index) => ({
  label: `${index + 1}`,
  value: index >= 9 ? `${index + 1}` : `0${index + 1}`,
}));

const currentYear = dayjs().year();
const years = new Array(12).fill(1).map((_, index) => ({
  label: `${index + currentYear}`,
  value: `${index + currentYear}`,
}));

const microformStyles = {
  input: {
    'font-size': '14px',
    'font-family': 'helvetica, tahoma, calibri, sans-serif',
    color: '#555',
    height: '10px',
  },
  ':focus': { color: 'blue' },
  ':disabled': { cursor: 'not-allowed' },
  valid: { color: '#3c763d' },
  invalid: { color: '#a94442' },
};

interface CybersourceFormValues {
  month?: string;
  year?: string;
}

function CyberSource() {
  const dispatch = useAppDispatch();
  const payment = useSelector(selectPayment);
  const currentFlow = useSelector(selectCurrentFlow);
  const pciEnabled = useSelector(selectPciPaymentApiEnabled);
  const api = useMemo(() => buildPaymentApi(pciEnabled), [pciEnabled]);
  const placeNavigate = usePlaceNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const paymentId = params.get('paymentId') as string;
  const amount = convertNumberToCurrency(params.get('amount') || 0);
  const place = params.get('place');
  const [error, setError] = useState<string>();
  const [posted, setPosted] = useState<boolean>(false);
  const microform = useRef<MicroFormPlaceholder>(null);

  const formValidation = useCallback(
    (values: CybersourceFormValues): FormikErrors<CybersourceFormValues> => {
      const errors: FormikErrors<CybersourceFormValues> = {};
      if (!values.year) {
        errors.year = FormValidations.ERROR_REQUIRED;
      }
      if (!values.month) {
        errors.month = FormValidations.ERROR_REQUIRED;
      }
      return errors;
    },
    []
  );

  const onSubmit = useCallback(
    (values: CybersourceFormValues & CustomPaymentData) => {
      setPosted(true);

      const options = {
        expirationMonth: values.month as string,
        expirationYear: values.year as string,
      };

      microform.current?.createToken(
        options,
        async (err: { reason: string }, transientToken: string) => {
          if (err) {
            console.error('Error generating the token', err);

            if (
              CybersourceConstants.TOKEN_CREATION_TIMEOUT_REASONS.includes(
                err.reason
              )
            ) {
              setErrorMicroform(CybersourceConstants.ERROR_TIMEOUT_MICROFORM);
            } else {
              setError(CybersourceConstants.ERROR_TOKEN);
            }
          } else {
            try {
              let address = values.address as string;
              if (values.address2) {
                address += ', ' + values.address2;
              }
              const result = await api.cybersourcePostback({
                ...values,
                transientToken,
                paymentId,
                address,
                country: 'US',
              });

              if (result.status === PostbackStatus.COMPLETE) {
                dispatch(
                  checkPaymentAsync(paymentId, currentFlow, () => {
                    if (currentFlow === FLOWS_ENUM.PAY_TOT) {
                      placeNavigate('/tax-success');
                    } else {
                      placeNavigate('/payment-success');
                    }
                  })
                );
              } else {
                placeNavigate('/payment-error');
                console.error('Error executing the payment', result);
              }
            } catch (e) {
              console.error(e);
              if (typeof e === 'string') {
                setError(e);
              } else if (e instanceof Error) {
                setError(e.message);
              }
            }
          }
          setPosted(false);
        }
      );
    },
    [currentFlow, paymentId]
  );

  const [errorMicroform, setErrorMicroform] = useState<string>();

  const { clientLibrary, clientLibraryIntegrity } = useMemo(() => {
    const context = payment.formData
      ? (payment.formData as { captureContext: string }).captureContext
      : undefined;
    if (context) {
      const decoded = jwtDecode<{
        ctx: Array<{
          data: { clientLibrary: string; clientLibraryIntegrity: string };
        }>;
      }>(context);
      return decoded['ctx'][0]['data'];
    }
    setErrorMicroform(CybersourceConstants.ERROR_MICROFORM);
    return { clientLibrary: undefined, clientLibraryIntegrity: undefined };
  }, [payment.formData, setErrorMicroform]);

  const errorInData = useMemo(() => {
    if (!paymentId || !params.get('amount') || !place) {
      return 'Not enough information to process the payment.';
    }
    return null;
  }, [paymentId, place, params]);

  const microformScriptLoaded = useCallback(async () => {
    const Flex = window.Flex;
    const context = payment?.formData
      ? (payment.formData as { captureContext?: string }).captureContext
      : undefined;

    if (!context) {
      setErrorMicroform(CybersourceConstants.ERROR_MICROFORM);
      console.error(
        `Context was not properly loaded: ${CybersourceConstants.ERROR_MICROFORM}`
      );
      return;
    }
    try {
      const flex = new Flex(context);
      microform.current = flex.microform({ styles: microformStyles });
      const number = microform.current?.createField('number', {
        placeholder: 'Card number',
      });
      const securityCode = microform.current?.createField('securityCode', {
        placeholder: '•••',
      });
      number?.load('#number-container');
      securityCode?.load('#securityCode-container');
    } catch (e) {
      setErrorMicroform(CybersourceConstants.ERROR_MICROFORM);
      console.error('An error was produced loading the data for the card', e);
    }
  }, [payment]);

  const helmetChange = useCallback(
    (_: unknown, addedTags: HelmetTags) => {
      if (addedTags && addedTags.scriptTags) {
        const scriptTag = addedTags.scriptTags[0];
        scriptTag.onload = microformScriptLoaded;
      }
    },
    [microformScriptLoaded]
  );

  const { isAbleToCloseWindow, closeWindow } = useCloseWindow();

  if (errorInData || errorMicroform) {
    return (
      <GeneralPageLayout
        showNext={isAbleToCloseWindow}
        next="Close Window"
        onNext={closeWindow}
      >
        {errorInData || errorMicroform}
      </GeneralPageLayout>
    );
  }

  return (
    <GeneralPageLayout>
      <Helmet
        script={[
          {
            src: clientLibrary,
            integrity: clientLibraryIntegrity,
            crossorigin: 'anonymous',
          },
        ]}
        onChangeClientState={helmetChange}
      />
      <CustomPaymentForm
        error={error}
        heading={`Amount Due: $${amount}`}
        loading={posted}
        onSubmit={onSubmit}
        submitText={`Pay $${amount}`}
        additionalInitialValues={{
          year: years[0].value,
          month: months[0].value,
        }}
        additionalValidation={formValidation}
      >
        {(
          formik: FormikHelpers<FormikValues & CybersourceFormValues> & {
            values: FormikValues & CybersourceFormValues;
            errors: FormikErrors<FormikValues & CybersourceFormValues>;
          }
        ) => {
          const setYear = (value: string | null) =>
            formik.setFieldValue('year', value);

          const setMonth = (value: string | null) =>
            formik.setFieldValue('month', value);

          return (
            <>
              {!errorMicroform && <MemoizedCard />}
              <CustomPaymentFormElements.FormGroup>
                <CustomPaymentFormElements.Label htmlFor="expMonth">
                  Expiry Month
                </CustomPaymentFormElements.Label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <CustomPaymentFormElements.FormGroup>
                    <CustomPaymentFormElements.Select
                      onChange={setMonth}
                      value={formik.values.month}
                      data={months}
                      searchable={false}
                      cleanable={false}
                    />
                    <CustomPaymentFormElements.Error
                      error={formik.errors.month}
                    />
                  </CustomPaymentFormElements.FormGroup>
                  <CustomPaymentFormElements.FormGroup>
                    <CustomPaymentFormElements.Select
                      data={years}
                      searchable={false}
                      onChange={setYear}
                      value={formik.values.year}
                      cleanable={false}
                      data-testid="year-select"
                    />
                    <CustomPaymentFormElements.Error
                      error={formik.errors.year}
                    />
                  </CustomPaymentFormElements.FormGroup>
                </div>
              </CustomPaymentFormElements.FormGroup>
            </>
          );
        }}
      </CustomPaymentForm>
    </GeneralPageLayout>
  );
}

const MemoizedCard = memo(function MemoizedCard() {
  return (
    <>
      <CustomPaymentFormElements.FormGroup>
        <CustomPaymentFormElements.Label id="cardNumber-label">
          Card Number
        </CustomPaymentFormElements.Label>
        <CustomPaymentFormElements.FieldContainer id="number-container" />
      </CustomPaymentFormElements.FormGroup>
      <CustomPaymentFormElements.FormGroup>
        <CustomPaymentFormElements.Label htmlFor="securityCode-container">
          Security Code
        </CustomPaymentFormElements.Label>
        <CustomPaymentFormElements.FieldContainer id="securityCode-container" />
      </CustomPaymentFormElements.FormGroup>
    </>
  );
});

export default CyberSource;
